import React, { FC, useCallback, useState, memo } from 'react';
import axios from 'axios';
import { v4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { StepAction } from 'Components/buttons/stepButton';
import { StepDisplay } from 'Components/step'; //eslint-disable-line
import { setFormCompletionTrue } from '@store/formCompletion/actions';
import terminateSession from '@store/terminateSession/actions';
import { setCurrentStep } from '@store/currentStep/actions';
import { setFormState } from '@store/formState/actions';
import { resetSharedValueState } from '@store/formSharedValues/actions';
import { setErrorState, resetErrorState } from '@store/error/actions';
import {
  enableStepProgression,
  disableStepProgression,
} from '@store/stepsProgression/actions';
import { addCollegePill } from '@store/collegePill/actions';
import { setPersonalizationComplete } from '@src/store/personalization/actions';
// import mapApolloMutationAsProp from '../../hoc/apolloWrapper/mapApolloMutationAsProp';
import ComparisonDescriptor from 'Utils/comparisonDescriptor'; //eslint-disable-line
import { areEqualObjects } from 'Utils/lodashUtils'; //eslint-disable-line
import WithFormData from 'HOC/WithFormData'; //eslint-disable-line
import WithMutation from 'HOC/WithMutation'; //eslint-disable-line
import { AppilyAuthContainer } from 'Containers/appilyAuth';
import dataTransform from './dataTransform';
import { MUTATION_SAVE_FIELD_DATA } from '../../apollo/mutations';
import SuccessPage from '../succesPage';
import FORM_STATE from '../../constants/formStateConstants';
import APPILY_TYPE from '../../constants/appilyTypeConstants';
import { isModuleEnabledForPlatform } from '../../constants/locationModuleConstants';

const calcProgresspercentage = (currentStep: number, numberofSteps: number) =>
  (currentStep / numberofSteps) * 100;

/**
 * Hide Next Button
 * Handle fields that do not require next button
 * Visitor Type and dynmaic vistor types do not require next.
 * Test for presence in step fieldIds
 */
const hideNextButton = (fields: any[]) =>
  fields.every((field: any) => field.inputType === 'buttonList');

const mapFieldIds = (fieldKey: string) =>
  fieldKey === 'school' ? 'school_ceeb_code' : fieldKey;

const StepContainer: FC<any> = memo((props: any) => {
  const dispatch = useDispatch();

  const state = useSelector((selectors: any) => selectors);
  const { personalizationCompleted } = state;

  const [stepInputState, setStepInputState] = useState({
    gdpr_compliance: false,
    marketing_compliance: false,
  });

  const successPageCopy = {
    earlyTermination: {
      title: 'Thanks and enjoy the ride!',
      description: 'Personalizing for you…',
    },
    collegeSearch: {
      title: 'You did it!',
      subTitle: 'Get ready to supercharge your search',
      description: 'Loading YouVisit experiences...',
    },
  };

  const updateInputState = (data: any) => {
    // How can I re-use this data?  in useMutation?
    setStepInputState(stateData => ({ ...stateData, ...data }));
  };

  const checkedCompliance =
    stepInputState.gdpr_compliance && stepInputState.marketing_compliance;

  // Function that applies 'constrain_by' field to select list such that
  // options are properly constrained by other fields in same step
  // NOTE: Only works when fields are in same step
  // Dependencies between fields across steps requires use of store
  // Will need separate implementation
  const constrainList = (item: any) => {
    if (!item.constrain_by) {
      return item;
    }

    const parsedContrainByParam = JSON.parse(item.constrain_by);
    const constrainByKeys = Object.keys(parsedContrainByParam);

    // If item's list is not constrained, return item as is
    if (constrainByKeys.length === 0) {
      return item;
    }
    // Assumes there is only one constraining attribute
    const constrainingAttribute = constrainByKeys[0];

    // If item which constrains list has no value yet, return as is
    if (!stepInputState[constrainingAttribute]) {
      return item;
    }
    const constrainingConditions = parsedContrainByParam[constrainingAttribute];
    // If item which constrains list has value, filter list options based on relevant operation
    const filterFunc = ComparisonDescriptor[constrainingConditions.type](
      constrainingConditions.comparator
    );

    // Curry w/ current state of constraining value
    const boundFilterFunc = filterFunc.bind(
      null,
      stepInputState[constrainingAttribute]
    );
    // Filter list items
    // Needs to create new object with object.assign.  Otherwise list items will remain filtered b/t updates
    const newItem = { ...item };
    const filteredList = newItem.list.filter(boundFilterFunc);
    // Applied filtered items as list to item
    return { ...item, list: filteredList };
  };

  const setCurrentStepWithDispatch = useCallback(
    (currentUserStep: number, stepAction: StepAction) => {
      dispatch(setCurrentStep(currentUserStep, stepAction));
    },
    [dispatch]
  );

  const setFormStateWithDispatch = useCallback(
    (stepValues: any) => {
      const formattedStepValues = stepValues.map((stepObject: any) => {
        return {
          fieldKey: Object.keys(stepObject)[0],
          fieldValue: Object.values(stepObject)[0],
        };
      });
      dispatch(setFormState(formattedStepValues));

      if (!personalizationCompleted) {
        const isPersonalizationStepComplete = formattedStepValues.some(
          (step: any) =>
            step.fieldKey === FORM_STATE.personalizationPreferences ||
            step.fieldKey === FORM_STATE.personalizationSuggestions
        );
        dispatch(setPersonalizationComplete(isPersonalizationStepComplete));
      }
    },
    [dispatch, personalizationCompleted]
  );

  const setErrorStateWithDispatch = useCallback(
    (stepValues: any) => {
      const formattedStepValues = stepValues.map((stepObject: any) => ({
        fieldKey: Object.keys(stepObject)[0],
        fieldValue: Object.values(stepObject)[0],
      }));
      dispatch(setErrorState(formattedStepValues));
    },
    [dispatch]
  );

  const resetErrorStateWithDispatch = useCallback(() => {
    dispatch(resetErrorState());
  }, [dispatch]);

  const endSession = useCallback(
    (errorMessage: string) => {
      const { userkey } = state.analytics.analytics;
      const { locId } = state.tour;
      axios.get(
        `${process.env.REACT_APP_BASE_URL}tour/Registration/removeRegistrationData`,
        {
          withCredentials: true,
          params: {
            requestid: v4(),
            userkey,
            loc_id: locId,
          },
        }
      );
      dispatch(terminateSession(errorMessage));
    },
    [dispatch, state.terminateSession.errorMessage]
  );

  const setCompletedWithDispatch = useCallback(
    (stepAction: StepAction) => {
      dispatch(setFormCompletionTrue(stepAction));
      // now that we are completed, wait 1 second and handle redirect
      const redirectUrl = props.data.redirect_url;
      if (redirectUrl) {
        setTimeout(() => {
          // Let's open in a new tab?
          if (props.data.redirect_newtab || !props.tour.isStandalone) {
            window.open(redirectUrl, '_blank');
          } else {
            window.location.href = redirectUrl;
          }
        }, 1000);
      }
    },
    [dispatch]
  );

  const enableStepProgressionWithDispatch = useCallback(() => {
    dispatch(enableStepProgression());
  }, [dispatch]);

  const disableStepProgressionWithDispatch = useCallback(() => {
    dispatch(disableStepProgression());
  }, [dispatch]);
  const resetSharedFormValuesWithDispatch = useCallback(() => {
    dispatch(resetSharedValueState());
  }, [dispatch]);

  const addCollegePillWithDispatch = (pill: any) => {
    dispatch(addCollegePill({ value: pill.value, label: pill.label }));
  };

  const transformed = dataTransform(state, props);

  // College search copy //
  const fromCollegeSearch = Number(state.tour.fromSearch);
  let successTitle = fromCollegeSearch
    ? successPageCopy.collegeSearch.title
    : transformed.successTitle;

  let successSubtitle = fromCollegeSearch
    ? successPageCopy.collegeSearch.subTitle
    : transformed.successSubtitle;

  let successDescription = fromCollegeSearch
    ? successPageCopy.collegeSearch.description
    : transformed.successDescription;

  if (state.terminateSession.terminate) {
    successTitle = successPageCopy.earlyTermination.title;
    successSubtitle = state.terminateSession.errorMessage;
    successDescription = successPageCopy.earlyTermination.description;
  }

  const hasAppilyIframe = isModuleEnabledForPlatform(
    state.tour.locationModules,
    APPILY_TYPE.APPILY_IFRAME,
    state.tour.platform
  );
  const hasAppiilyAuth = isModuleEnabledForPlatform(
    state.tour.locationModules,
    APPILY_TYPE.APPILY,
    state.tour.platform
  );
  const formType =
    state.tour.formType || state.analytics?.registration?.formType;
  const isSafari = state.device?.get('isPlatformSafari');

  if (
    formType === APPILY_TYPE.APPILY_FORM &&
    (hasAppilyIframe || hasAppiilyAuth) &&
    state?.tour?.cappexId &&
    state?.tour?.flexRegId
  ) {
    return (
      <AppilyAuthContainer
        // eslint-disable-next-line camelcase
        formTitle={props.data?.institution_name}
        formSubTitle={props.data?.subtitle}
        // eslint-disable-next-line camelcase
        logo={props.data?.large_form_image}
        appilyMode={
          hasAppilyIframe && !isSafari
            ? APPILY_TYPE.APPILY_IFRAME
            : APPILY_TYPE.APPILY
        }
      />
    );
  }

  // This is mapStateToProps
  // TODO:  if this works, then find which particular dependencies datatransform relies on.

  return !state.completed &&
    !state.terminateSession.terminate &&
    state.tour.step !== 'success' ? (
    // eslint-disable-next-line react/jsx-indent
    <StepDisplay
      terminateSession={(errorMessage: string) => endSession(errorMessage)}
      hideNextButton={hideNextButton}
      setStepInputState={updateInputState}
      stepInputState={stepInputState}
      calcProgresspercentage={calcProgresspercentage}
      checkedCompliance={checkedCompliance}
      mapFieldIds={mapFieldIds}
      constrainList={constrainList}
      setCurrentStep={setCurrentStepWithDispatch}
      setFormState={setFormStateWithDispatch}
      setErrorState={setErrorStateWithDispatch}
      resetErrorState={resetErrorStateWithDispatch}
      setCompleted={setCompletedWithDispatch}
      enableStepProgression={enableStepProgressionWithDispatch}
      disableStepProgression={disableStepProgressionWithDispatch}
      resetSharedFormValues={resetSharedFormValuesWithDispatch}
      addCollegePill={addCollegePillWithDispatch}
      {...transformed}
      mutate={props.mutate}
    />
  ) : (
    <SuccessPage
      completed={state.completed}
      success_title={successTitle}
      success_subtitle={successSubtitle}
      success_description={successDescription}
      tour={state.tour}
      stepInputState={stepInputState}
    />
  );
}, areEqualObjects);

// WithFormData WithMutation
export default WithMutation(
  WithFormData(WithFormData(StepContainer)),
  MUTATION_SAVE_FIELD_DATA
);

// export default mapApolloMutationAsProp(StepContainer, MUTATION_SAVE_FIELD_DATA);

/*
export default mapApolloMutationAsProp(
  connect(mapStateToProps, mapDispatchToProps)(StepDisplay),
  MUTATION_SAVE_FIELD_DATA
);
*/
